/**
 * Copy text to clipboard with a legacy fallback.
 *
 * Uses the modern Clipboard API when available (secure contexts only), and
 * falls back to `document.execCommand('copy')` for older browsers, http pages
 * (e.g. a LAN dev host), and edge cases where the Clipboard API is blocked.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to legacy path
  }

  try {
    return execCopy(text) || execCopyWithSelection(text);
  } catch {
    return false;
  }
}

// The fallback writes the text from inside the `copy` event rather than relying on a
// selected textarea: an open modal's focus trap steals focus from anything outside it,
// so the selection was lost and the copy "succeeded" with nothing copied.
function execCopy(text: string): boolean {
  let copied = false;
  const onCopy = (event: ClipboardEvent) => {
    if (!event.clipboardData) return;
    event.clipboardData.setData('text/plain', text);
    event.preventDefault();
    copied = true;
  };
  document.addEventListener('copy', onCopy);
  try {
    document.execCommand('copy');
  } finally {
    document.removeEventListener('copy', onCopy);
  }
  return copied;
}

// Safari only fires `copy` when something is selected, so select a throwaway node,
// placed inside the open dialog (if any) where its focus trap leaves it alone.
function execCopyWithSelection(text: string): boolean {
  const host = document.activeElement?.closest('[role="dialog"]') ?? document.body;
  const node = document.createElement('span');
  node.textContent = text;
  node.style.position = 'fixed';
  node.style.opacity = '0';
  node.style.whiteSpace = 'pre';
  host.appendChild(node);
  const selection = window.getSelection();
  const range = document.createRange();
  range.selectNodeContents(node);
  selection?.removeAllRanges();
  selection?.addRange(range);
  try {
    return execCopy(text);
  } finally {
    selection?.removeAllRanges();
    host.removeChild(node);
  }
}
